import * as React from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router';
import { Spinner } from '@quality24/design-system';

import RootLayout from '@/inpatient-dashboard/templates/RootLayout/RootLayout';
import ErrorBoundary from '@/inpatient-dashboard/organisms/RouteErrorBoundary';
import AppContainer from '../AppContainer';
import PrivateRoute from './PrivateRoute';
import allGroups, { DashboardMenuGroup } from './groups';
import ProvisionPage from '../pages/ProvisionPage';

const HydrateFallback = () => (
  <div className="d-flex align-items-center justify-content-center p-4">
    <Spinner />
  </div>
);

const getRoutesFromGroups = (
  groups: DashboardMenuGroup[],
  path:
    | '/main/'
    | '/surveys/'
    | '/admin/'
    | '/feedbacks/'
    | '/exports/'
    | undefined = '/main/',
): RouteObject[] => {
  const routes: RouteObject[] = [];
  groups
    .flatMap((g) => [...g.items])
    .forEach((route) => {
      const routeParts = route.to.split(path)[1];

      if (!routeParts) {
        return;
      }

      routes.push({
        path: routeParts,
        lazy: route.lazy,
        ErrorBoundary,
      });
    });
  return routes;
};

export const router = [
  {
    element: <AppContainer />,
    HydrateFallback,
    children: [
      {
        path: '/',
        ErrorBoundary,
        children: [
          {
            path: 'provision',
            element: <ProvisionPage />,
          },
          {
            path: '/main',
            element: <RootLayout />,
            children: [
              {
                index: true,
                lazy: () => import('./HomeRoute'),
              },
              // Dynamic routes based on groups
              ...getRoutesFromGroups(allGroups),
              // Fallback routes
              {
                path: '*',
                element: <Navigate to="/404" replace />,
              },
            ],
          },
          {
            path: '/surveys',
            element: <RootLayout />,
            children: [
              {
                index: true,
                lazy: () => import('./HomeRoute'),
              },
              // Dynamic routes based on groups
              ...getRoutesFromGroups(allGroups, '/surveys/'),
              // Fallback routes
              {
                path: '*',
                element: <Navigate to="/404" replace />,
              },
            ],
          },
          {
            path: '/feedbacks',
            element: <RootLayout />,
            children: [
              {
                index: true,
                lazy: () => import('./HomeRoute'),
              },
              // Dynamic routes based on groups
              ...getRoutesFromGroups(allGroups, '/feedbacks/'),
              // Fallback routes
              {
                path: '*',
                element: <Navigate to="/404" replace />,
              },
            ],
          },
          {
            path: '/exports',
            element: <Outlet />,
            children: [
              {
                index: true,
                lazy: () => import('./HomeRoute'),
              },
              // Dynamic routes based on groups
              ...getRoutesFromGroups(allGroups, '/exports/'),
              // Fallback routes
              {
                path: '*',
                element: <Navigate to="/404" replace />,
              },
            ],
          },
          {
            path: '/admin',
            element: <RootLayout />,
            children: [
              {
                index: true,
                lazy: () => import('./HomeRoute'),
              },
              // Dynamic routes based on groups
              ...getRoutesFromGroups(allGroups, '/admin/'),
              // Fallback routes
              {
                path: '*',
                element: <Navigate to="/404" replace />,
              },
            ],
          },
          // General routes
          {
            path: '/unauthorized',
            element: <RootLayout />,
            children: [
              {
                index: true,
                lazy: async () => {
                  const { Component } = await import(
                    '@/inpatient-dashboard/pages/UnauthorizedPage/UnauthorizedPage'
                  );
                  return {
                    Component: () => (
                      <PrivateRoute>
                        <Component />
                      </PrivateRoute>
                    ),
                  };
                },
              },
            ],
          },
          {
            path: '404',
            element: <RootLayout />,
            children: [
              {
                index: true,
                lazy: async () => {
                  const { Component } = await import(
                    '@/inpatient-dashboard/pages/NotFoundPage/NotFoundPage'
                  );
                  return {
                    Component: () => (
                      <PrivateRoute>
                        <Component />
                      </PrivateRoute>
                    ),
                  };
                },
              },
            ],
          },
          {
            index: true,
            element: <Navigate to="/provision" replace />,
          },
          {
            path: '*',
            element: <Navigate to="/provision" replace />,
          },
        ],
      },
    ],
  },
] as RouteObject[];

export default router;
